import React, { useState } from "react";
import { Loader2Icon, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import FormBuilderSidebar from "./components/FormBuilderSidebar";
import FormBuilderCanvas from "./components/FormBuilderCanvas";
import FormBuilderPreview from "./components/FormBuilderPreview";
import FormTitleAndDescription from "./components/form-builder/FormTitleAndDescription";
import SaveStatusIndicator from "./components/SaveStatusIndicator";
import { useFormBuilder } from "./hooks/useFormBuilder";
import { Button } from "../../components/ui/button";
import { TooltipWrapper } from "../../components/ui/tooltip-wrapper";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import RulesList from "./components/rules/RulesList";

export const FormBuilderApp: React.FC = () => {
	const {
		activeForm,
		activeSection,
		updateFormMeta,
		addSection,
		updateSection,
		deleteSection,
		handleSectionReorder,
		setActiveSection,
		saveStatus,
		lastSaved,
		isLoading,
		togglePublishState,
		addRule,
		updateRule,
		deleteRule,
	} = useFormBuilder();
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState<"form" | "rules" | "preview">(
		"form"
	);

	// If data is still loading, show a spinner
	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-screen">
				<Loader2Icon className="h-8 w-8 animate-spin text-gray-400" />
			</div>
		);
	}

	// If no form is active (meaning no form ID in URL or form doesn't exist), redirect to forms list
	if (!activeForm) {
		return (
			<div className="flex flex-col items-center justify-center h-screen gap-4">
				<h2 className="text-xl font-semibold text-gray-700">
					Formular konnte nicht geladen werden
				</h2>
				<Button
					onClick={() => navigate("/auftragsformulare")}
					variant="outline"
				>
					Zurück zur Übersicht
				</Button>
			</div>
		);
	}

	return (
		<div className="flex flex-col mb-12">
			<div className="sticky top-0 z-10 bg-white flex flex-row justify-between items-center gap-4 p-2 mb-4 border-b border-gray-200">
				<div className="flex flex-row items-center gap-3 flex-grow">
					{/* Back button */}
					<TooltipWrapper
						title="Zurück zur Formularliste"
						position="right"
					>
						<Button
							variant="ghost"
							size="icon"
							onClick={() => navigate("/auftragsformulare")}
							className="flex-shrink-0"
							aria-label="Zurück zur Formularliste"
						>
							<ArrowLeft className="h-5 w-5" />
						</Button>
					</TooltipWrapper>
					<div className="flex-shrink-0">
						<h2 className="text-lg font-medium">
							{activeForm.title}
						</h2>
					</div>
					<Tabs
						value={activeTab}
						onValueChange={(value) =>
							setActiveTab(value as "form" | "rules" | "preview")
						}
						className="flex-shrink-0"
					>
						<TabsList>
							<TabsTrigger value="form">Formular</TabsTrigger>
							<TabsTrigger value="rules">Regeln</TabsTrigger>
							<TabsTrigger value="preview">Vorschau</TabsTrigger>
						</TabsList>
					</Tabs>
				</div>

				<div className="flex-shrink-0 flex items-center gap-2">
					{/* Save status indicator */}
					<SaveStatusIndicator
						status={saveStatus}
						lastSaved={lastSaved}
					/>

					{/* Publish/Unpublish button */}
					<TooltipWrapper
						title={
							activeForm.published_at
								? "Zurückziehen"
								: "Veröffentlichen"
						}
					>
						<Button
							variant={
								activeForm.published_at ? "outline" : "default"
							}
							onClick={() => togglePublishState()}
							size="sm"
							className={
								activeForm.published_at
									? "border-green-500 text-green-600"
									: ""
							}
						>
							{activeForm.published_at
								? "Zurückziehen"
								: "Veröffentlichen"}
						</Button>
					</TooltipWrapper>
				</div>
			</div>

			<div className="flex flex-col px-4 gap-2">
				{activeTab === "form" && (
					<div className="w-full max-w-xl mx-auto">
						<FormTitleAndDescription
							title={activeForm.title}
							description={activeForm.description || ""}
							backgroundColor={activeForm.bg_color || undefined}
							onUpdateMeta={updateFormMeta}
						/>

						<div className="flex flex-col gap-4 mt-4">
							<FormBuilderSidebar
								sections={activeForm.definition.sections || []}
								activeSection={activeSection || ""}
								onSelectSection={setActiveSection}
								onAddSection={addSection}
								onDeleteSection={deleteSection}
								onReorderSections={handleSectionReorder}
							/>

							<FormBuilderCanvas
								formTemplate={activeForm}
								activeSection={activeSection || ""}
								onUpdateSection={updateSection}
							/>
						</div>
					</div>
				)}

				{activeTab === "rules" && (
					<RulesList
						rules={activeForm.rules || []}
						onAddRule={addRule}
						onUpdateRule={updateRule}
						onDeleteRule={deleteRule}
						formSections={activeForm.definition.sections}
					/>
				)}

				{activeTab === "preview" && (
					<FormBuilderPreview formTemplate={activeForm} />
				)}
			</div>
		</div>
	);
};
